import React, { cloneElement, ReactElement, useEffect, useRef, useState } from 'react'

import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { StripeCardElement } from '@stripe/stripe-js'
import { loadStripe } from '@stripe/stripe-js/pure'
import { useForm } from 'react-hook-form'
import { AutorenewRounded } from '@mui/icons-material'
import { v4 as uuidv4 } from 'uuid'

import { useAppSelector } from '../../../store/hooks'
import { selectGuestId } from '../../../store/user/userSlice'

import useToasterHelper from '../../../helpers/ToasterHelper'

import { IUserInterface, IUserInterfaceExtended } from '../../../models/users/user.interface'

import { httpService } from '../../../services/httpService'
import FirestoreService from '../../../services/firestoreService'
import MainModal from '../../modals/common/MainModal'
import { Skeleton } from '../../ui/skeleton'

import { UserModel } from '../../../models/users/user.model'
import { ICompetitorCard, Props } from '../../../types/competitor_types'

import { MESSAGES_CONST } from '../../../const/messages-const'
import { MODAL_CONSTS } from '../../../const/modal-const'
import { CONST } from '../../../const/const'

interface ICreateCardPayload {
  pmId: string
  stripeId: string
  userStripeAccountId: string
}

interface AddCardProps {
  dataToPassOn: IDataToPassOn
  footer?: ReactElement
  className?: string
  ref?: React.Ref<HTMLButtonElement>
  onCloseModal: () => void
}

interface AddUserCardProps {
  handleModal?: any
  dataToPassOn: IDataToPassOn
  footer?: ReactElement
  className?: string
  customRef?: React.Ref<HTMLButtonElement>
  userStripeAccountId: string
  onCloseModal: () => void
}

interface IDataToPassOn {
  re_open_modal?: boolean
  modal_name?: string
  organizerId?: string
  // "isApproved" is true, then we must provide access to the user card if the card is added by the organizer
  isApproved?: boolean
  user: IUserInterface
  onSave?: () => Promise<void>
}

interface IUserProfileAccountTabAddCard extends Props {
  dataToPassOn: IDataToPassOn
}

interface ICheckoutForm {
  onCloseModal: () => void
  dataToPassOn: IDataToPassOn
}

interface ICheckOutFormFooter {
  loading?: boolean
  onCloseModal: () => void
}

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

export const AddCard = React.forwardRef<HTMLButtonElement, AddCardProps>((props, ref) => {
  const [organiserProfile, setOrganiserProfile] = useState<IUserInterfaceExtended | null>(null)

  useEffect(() => {
    const getOrganiser = async () => {
      const organiserSnapshots = await FirestoreService.getItem(
        COLLECTIONS.USERS.NAME,
        props?.dataToPassOn?.organizerId ?? ''
      )
      const owner = UserModel.fromFirestoreDoc(organiserSnapshots).toObject()
      setOrganiserProfile(owner)
    }
    if (props?.dataToPassOn?.organizerId) getOrganiser().then()
  }, [props?.dataToPassOn?.organizerId])

  if (!organiserProfile?.userStripeAccountId) {
    return (
      <div className="relative space-y-2">
        <Skeleton className="h-16 w-full rounded-lg" />
      </div>
    )
  }

  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY as string, {
    stripeAccount: organiserProfile?.userStripeAccountId || '',
  })

  return (
    <Elements stripe={stripePromise}>
      <AddUserCard
        footer={props.footer}
        className={props.className}
        dataToPassOn={props.dataToPassOn}
        onCloseModal={props.onCloseModal}
        userStripeAccountId={organiserProfile?.userStripeAccountId}
        customRef={ref ? ref : null}
      />
    </Elements>
  )
})

export const AddUserCard = React.forwardRef<HTMLButtonElement, AddUserCardProps>(
  ({ onCloseModal, footer, className, userStripeAccountId, dataToPassOn, customRef }) => {
    const stripe = useStripe()
    const elements = useElements()

    const { user } = dataToPassOn

    const [loading, setLoading] = useState(false)
    const toasterFunctions = useToasterHelper()
    const [manualError, setManualError] = useState<string>('')

    const guestId = useAppSelector(selectGuestId)

    const { handleSubmit, register } = useForm<ICompetitorCard>({
      mode: 'onChange',
    })
    const form = useRef<HTMLFormElement>(null)

    // Components
    const Footer = footer ? cloneElement(footer, { loading }) : null

    // Is fired on submitting
    const onSubmit = async (formData: any) => {
      setLoading(true)

      if (!stripe || !elements) {
        // Stripe.js has not yet loaded.
        setLoading(false)
        return
      }

      try {
        // Use elements.getElement to get a reference to the CardElement.
        const cardElement = elements.getElement(CardElement) as StripeCardElement

        // Use Stripe.js to handle the payment.
        const { error, paymentMethod } = await stripe?.createPaymentMethod({
          type: 'card',
          card: cardElement,
        })

        if (error) {
          setManualError(error?.message ?? '')

          setTimeout(() => {
            setManualError('')
          }, 3000)

          setLoading(false)
          return
        }

        const stripeCustomer = await httpService({
          url: `create_stripe_customer`,
          method: 'POST',
          data: {
            userEmail: user.userEmail ?? formData.cardHolderEmail,
            userStripeAccountId: userStripeAccountId,
          },
        })

        const customerId = stripeCustomer?.customer?.stripe?.id

        const createCardPayload: ICreateCardPayload = {
          pmId: paymentMethod?.id || '',
          stripeId: customerId,
          userStripeAccountId: userStripeAccountId || '',
        }

        const createCardRes = await httpService({
          url: 'create_card',
          method: 'POST',
          data: createCardPayload,
        })

        await FirestoreService.updateItem(
          CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.USERS.NAME,
          user?.id,
          {
            userStripeId: customerId,
            userStripeDefaultPmId: createCardRes?.paymentMethodAttached?.id,
          }
        )

        if (dataToPassOn?.isApproved) {
          await FirestoreService.updateItem(COLLECTIONS.USERS.NAME, user?.id, {
            paymentMethodsAccess: {
              ...user.paymentMethodsAccess,
              [createCardRes?.paymentMethodAttached?.id]: [
                {
                  requesterId: dataToPassOn?.organizerId,
                  status: 'approved',
                  cardNumber: createCardRes?.paymentMethodAttached?.card?.last4,
                  token: uuidv4(),
                  created: new Date().toString(),
                },
              ],
            },
          })
        }

        if (dataToPassOn?.onSave) await dataToPassOn.onSave()
        toasterFunctions.success({ message: MESSAGES_CONST.CARDADDED })
        onCloseModal()
      } catch (error: any) {
        console.error(error, 'error')
        toasterFunctions.error({
          message: error?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG,
        })
      } finally {
        setLoading(false)
      }
    }

    return (
      <form
        className={`w-full flex flex-col justify-evenly h-full ${className ?? ''}`}
        ref={form}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="relative py-6 px-4 cardView border border-SeabiscuitGray500ThemeColor rounded-[12px] ml-[-8px]">
          {!user.id && !guestId && (
            <>
              <div className="flex flex-col mb-3">
                <input
                  type="text"
                  required
                  className="pt-0 px-0 pb-4 w-full bg-transparent outline-0 ring-0 border-0 focus:outline-0 focus:ring-0 text-black cursor-pointer border-b !border-b-[#D3DAEE]"
                  placeholder="Name on Card"
                  {...register(`cardHolderName`)}
                />
              </div>
              <div className="flex flex-col mb-6">
                <input
                  type="email"
                  required
                  className="pb-4 px-0 w-full bg-transparent outline-0 ring-0 border-0 focus:outline-0 focus:ring-0 text-black cursor-pointer border-b !border-b-[#D3DAEE]"
                  placeholder="Card holder email"
                  {...register(`cardHolderEmail`)}
                />
              </div>
            </>
          )}
          <CardElement />
          {manualError && (
            <div className="text-SeabiscuitMainThemeColor text-[12px] mt-2 absolute bottom-0 left-0 translate-y-full">
              {manualError}
            </div>
          )}{' '}
        </div>
        <button className="hidden" type="submit" disabled={!stripe} ref={customRef}></button>
        {Footer}
      </form>
    )
  }
)

const CheckOutFormFooter = (props: ICheckOutFormFooter) => {
  return (
    <div className="ActionButton mt-7">
      <button
        disabled={props.loading}
        type="submit"
        className="w-full mb-2 h-12 m-auto flex items-center justify-center py-2 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-SeabiscuitMainThemeColor focus-visible:outline-none disabled:text-SeabiscuitLightTextColor disabled:bg-SeabiscuitLightThemeColor"
      >
        {props.loading ? <AutorenewRounded fontSize="small" className="animate-spin" /> : 'SAVE'}
      </button>

      <button
        type="button"
        onClick={() => props?.onCloseModal()}
        className="w-full h-12 m-auto flex items-center justify-center py-2 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-SeabiscuitLightTextColor bg-SeabiscuitLightThemeColor"
      >
        CANCEL
      </button>
    </div>
  )
}

const CheckoutForm = (props: ICheckoutForm) => {
  return (
    <MainModal
      title="Add card"
      show={true}
      type="CARD"
      className="mt-4 z-[50]"
      modalClassName="z-[60]"
      overlayClassName="z-[59]"
      setHeightAsPerContent={true}
      size="sm"
      onHide={props.onCloseModal}
    >
      <AddCard
        onCloseModal={props.onCloseModal}
        dataToPassOn={props.dataToPassOn}
        footer={<CheckOutFormFooter onCloseModal={props.onCloseModal} />}
      />
    </MainModal>
  )
}

const UserProfileAccountTabAddCard = (props: IUserProfileAccountTabAddCard) => {
  const onCloseModal = () => {
    if (props?.dataToPassOn?.re_open_modal && props?.dataToPassOn?.modal_name)
      props?.handleModal?.(true, props?.dataToPassOn?.modal_name, props?.dataToPassOn)

    props?.handleModal?.(false, MODAL_CONSTS.CARD, props?.dataToPassOn)
  }

  return <CheckoutForm onCloseModal={onCloseModal} dataToPassOn={props.dataToPassOn} />
}

export default UserProfileAccountTabAddCard
